import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef } from 'react';

import BoxButton from '../buttons/BoxButton';

import styles from '../../../styles/partials/box-carousel.module.scss';

const propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            title: PropTypes.string.isRequired,
            description: PropTypes.string,
        }),
    ),
    currentIndex: PropTypes.number,
    onClick: PropTypes.func,
    className: PropTypes.string,
};

const defaultProps = {
    items: null,
    currentIndex: null,
    onClick: null,
    className: null,
};

function BoxCarousel({ items, currentIndex, onClick, className }) {
    const containerRef = useRef();
    const refs = useRef([]);
    const activeIndex = useMemo(
        () => items.findIndex((it, idx) => idx === currentIndex),
        [items, currentIndex],
    );

    useEffect(() => {
        if (activeIndex !== undefined) {
            const el = refs.current[activeIndex] || null;

            if (el !== null) {
                const { left: itemLeft = 0 } = el.getBoundingClientRect();
                const currentLeft = containerRef.current.scrollLeft;
                const left = currentLeft + itemLeft;
                containerRef.current.scroll({
                    left,
                    behavior: 'smooth',
                });
            }
        }
    }, [activeIndex]);

    return (
        <div
            className={classNames({
                [styles.container]: true,
                [className]: className !== null,
            })}
            ref={containerRef}
        >
            <div className={styles.track}>
                {(items || []).map((it, index) => {
                    const { id = null, title = null, label = null } = it || {};
                    return (
                        <div
                            className={styles.item}
                            key={`box-${id}-${index + 1}`}
                            ref={(i) => {
                                refs.current[index] = i;
                            }}
                        >
                            <BoxButton
                                key={`${id}-${title}-${index + 1}`}
                                active={index === currentIndex}
                                onClick={
                                    onClick !== null
                                        ? (e) => {
                                              e.stopPropagation();
                                              onClick(it, index);
                                          }
                                        : null
                                }
                                className={styles.button}
                            >
                                <h6 className={styles.title}>{title}</h6>
                                <p className={styles.description}>{label}</p>
                            </BoxButton>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

BoxCarousel.propTypes = propTypes;
BoxCarousel.defaultProps = defaultProps;

export default BoxCarousel;
